import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Repeat, Calendar, Users, DollarSign, AlertCircle, ChevronRight, Mail, Phone } from 'lucide-react';
import { seriesAPI } from '../../api/bookings';
import { formatDays, formatTimeRange, customerInfo } from './BookingSeries/seriesHelpers';

function StatusBadge({ status }) {
  const cfg = {
    active: { label: 'Active', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
    cancelled: { label: 'Cancelled', cls: 'bg-rose-100 text-rose-800 border-rose-300' },
    completed: { label: 'Completed', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  }[status] || { label: status, cls: 'bg-slate-100 text-slate-700 border-slate-300' };
  return <Badge variant="outline" className={cfg.cls}>{cfg.label}</Badge>;
}

export default function BookingSeries() {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await seriesAPI.listSeries();
      setSeries(rows || []);
    } catch (e) {
      toast.error('Failed to load series');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCancelAll = async (s) => {
    // From the list view we can't surface a checkbox without a custom
    // dialog — default to notify=true to stay safe; staff who want to
    // skip the email use the Series Detail page where the toggle lives.
    if (!window.confirm(
      `Cancel all ${s.successful_occurrences} bookings in this series?\n\nThe customer will be emailed a cancellation summary.`
    )) return;
    try {
      const res = await seriesAPI.cancelSeries(s.id, { mode: 'all', notify_customer: true });
      toast.success(`Cancelled ${res.cancelled_bookings} booking(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel series');
    }
  };

  return (
    <div data-testid="booking-series-page" className="max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-4xl font-black font-heading uppercase">Recurring Series</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Weekly and biweekly recurring bookings. Each series creates individual bookings that respect
            holiday and Friday-prayer rules.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading series…</div>
      ) : series.length === 0 ? (
        <Card className="p-8 text-center" data-testid="series-empty">
          <Repeat className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
          <p className="text-muted-foreground mb-1 font-semibold">No recurring series yet</p>
          <p className="text-xs text-muted-foreground">
            Create one from <strong>Manual Booking → Recurring Series</strong> mode.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {series.map((s) => {
            const ci = customerInfo(s);
            return (
            <Card
              key={s.id}
              className="px-4 py-3 border-l-4 border-l-emerald-500 space-y-2"
              data-testid={`series-card-${s.id}`}
            >
              {/* Row 1 — id + status + pattern + actions + counts */}
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                    #{s.id.slice(0, 8).toUpperCase()}
                  </span>
                  <StatusBadge status={s.status} />
                  <span className="text-[10px] uppercase font-bold tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                    {s.recurrence?.pattern || '—'}
                  </span>
                  <span className="text-[10px] uppercase font-semibold text-muted-foreground">
                    {s.duration}h
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right leading-tight">
                    <span className="text-base font-black text-emerald-700" data-testid={`series-bookings-${s.id}`}>
                      {s.successful_occurrences || 0}
                    </span>
                    <span className="text-[10px] uppercase font-bold text-muted-foreground ml-1">
                      bookings
                    </span>
                    {s.skipped_occurrences > 0 && (
                      <span className="ml-2 text-xs text-amber-700">
                        <AlertCircle className="w-3 h-3 inline" /> {s.skipped_occurrences} skipped
                      </span>
                    )}
                    <span className="ml-2 text-xs text-muted-foreground">
                      <DollarSign className="w-3 h-3 inline" />
                      ${(s.total_revenue_estimate || 0).toFixed(2)}
                    </span>
                  </div>
                  <Button
                    asChild
                    size="sm"
                    className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  >
                    <Link to={`/admin/bookings/series/${s.id}`} data-testid={`view-series-${s.id}`}>
                      View <ChevronRight className="w-3 h-3 ml-1" />
                    </Link>
                  </Button>
                  {s.status === 'active' && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-rose-700 border-rose-300 hover:bg-rose-50"
                      onClick={() => handleCancelAll(s)}
                      data-testid={`cancel-series-${s.id}`}
                    >
                      Cancel All
                    </Button>
                  )}
                </div>
              </div>

              {/* Row 2 — customer (single line) */}
              <div className="border-t pt-2 text-sm" data-testid={`series-customer-${s.id}`}>
                <span className="inline-flex items-center gap-1.5 font-semibold text-gray-900 mr-3">
                  <Users className="w-4 h-4" /> {ci.name}
                </span>
                {ci.email && (
                  <span className="inline-flex items-center gap-1 text-gray-600 mr-3">
                    <Mail className="w-3.5 h-3.5" /> {ci.email}
                  </span>
                )}
                {ci.phone && (
                  <span className="inline-flex items-center gap-1 text-gray-600">
                    <Phone className="w-3.5 h-3.5" /> {ci.phone}
                  </span>
                )}
              </div>

              {/* Row 3 — schedule (single line) */}
              <div className="border-t pt-2 text-sm flex items-center gap-3 flex-wrap text-gray-800">
                <span className="inline-flex items-center gap-1.5 font-semibold">
                  <Calendar className="w-4 h-4 text-gray-700" />
                  {s.recurrence?.start_date} → {s.recurrence?.end_date || `${s.recurrence?.end_after_count} occurrences`}
                </span>
                <span className="text-gray-400">·</span>
                <span>{formatDays(s.recurrence?.days_of_week)}</span>
                <span className="text-gray-400">·</span>
                <span>{formatTimeRange(s.time_slot, s.duration)}</span>
              </div>
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
